import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '@/api';
import translateService from '@/services/translateService';

// Helper function to sync language preference
const syncLanguagePreference = (user) => {
  if (user?.preferred_language) {
    const userLang = user.preferred_language;
    const currentLang = translateService.getLanguage();
    if (userLang !== currentLang) {
      translateService.setLanguage(userLang);
    }
  }
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      subscriptionStatus: null,
      isInitialized: false, // Track if auth state has been initialized

      // Initialize auth state from localStorage
      initialize: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isInitialized: true, isAuthenticated: false });
          return;
        }
        
        try {
          const res = await authAPI.getMe();
          set({ 
            user: res.data, 
            token, 
            isAuthenticated: true, 
            isInitialized: true 
          });
          syncLanguagePreference(res.data);
        } catch (err) {
          localStorage.removeItem('token');
          set({ 
            user: null, 
            token: null, 
            isAuthenticated: false, 
            isInitialized: true 
          });
        }
      },

      login: async (email, password, language = null) => {
        set({ isLoading: true, error: null });
        try {
          const res = await authAPI.login({ email, password, language });
          const { access_token, user, requires_plan_selection } = res.data;
          localStorage.setItem('token', access_token);
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          
          // Sync UI language with user's saved preference
          if (user?.preferred_language) {
            syncLanguagePreference(user);
          }
          
          return { success: true, requires_plan_selection, user };
        } catch (err) {
          set({ error: err.response?.data?.detail || 'Login failed', isLoading: false });
          return false;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const res = await authAPI.register(data);
          const { access_token, user, requires_plan_selection } = res.data;
          localStorage.setItem('token', access_token);
          set({ user, token: access_token, isAuthenticated: true, isLoading: false });
          return { success: true, requires_plan_selection };
        } catch (err) {
          set({ error: err.response?.data?.detail || 'Registration failed', isLoading: false });
          return false;
        }
      },

      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false, subscriptionStatus: null });
      },

      fetchUser: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isInitialized: true, isAuthenticated: false });
          return;
        }
        
        try {
          const res = await authAPI.getMe();
          set({ user: res.data, token, isAuthenticated: true, isInitialized: true });
          
          // Sync UI language with user's saved preference on page refresh/reload
          syncLanguagePreference(res.data);
        } catch (err) {
          localStorage.removeItem('token');
          set({ user: null, token: null, isAuthenticated: false, isInitialized: true });
        }
      },

      refreshUser: async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        try {
          const res = await authAPI.getMe();
          set({ user: res.data });
        } catch (err) {
          console.error('Failed to refresh user:', err);
        }
      },

      updateUser: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const res = await authAPI.updateMe(data);
          set({ user: res.data, isLoading: false });
          
          // If language was updated, sync UI
          if (data.preferred_language) {
            syncLanguagePreference(res.data);
          }
          
          return true;
        } catch (err) {
          set({ error: err.response?.data?.detail || 'Update failed', isLoading: false });
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token }),
    }
  )
);
